import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    Tooltip,
    IconButton,
    Chip,
    CircularProgress,
} from '@mui/material';
import {
    Add,
    OpenInNew,
    Edit,
    Archive,
    DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import {
    buildPermissionMap,
    hasRight,
} from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import api from 'state/axios';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';
import DeleteConfirm from 'components/DeleteConfirm';


const CollectionDefs = () => {
    useAuthCheck();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const user = useSelector((state) => state.user);

    const permissionMap = useMemo(
        () => buildPermissionMap(user?.permissions),
        [user?.permissions]
    );
    const canCreate = hasRight(permissionMap, "CollectionDefs", "create", false);
    const canEdit   = hasRight(permissionMap, "CollectionDefs", "update", false);
    const canDelete = hasRight(permissionMap, "CollectionDefs", "delete", false);
    const canOpen = hasRight(permissionMap, "CollectionDefs", "open", false);

    const scrollRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rowSelectionModel, setRowSelectionModel] = useState({
        type: 'include',
        ids: new Set(),
    });

    const [rows, setRows] = useState([]);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleteType, setDeleteType] = useState(null);
    const [idToDelete, setIdToDelete] = useState(null);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });
    

    useEffect(() => { 
        const fetchData = async () => {
            try {
                setLoading(true);

                const { data } = await api.get('/collectiondefs');
                setRows(data);
            } catch (err) {
                setError(err);
                toast.error(err.response?.data?.message || 'Failed to load collections!');
            } finally {
                setLoading(false);
            }
        } 

        fetchData();
    }, []);

    const handleDeleteClick = (id) => {
        setIdToDelete(id);
        setDeleteType('single');
        setConfirmDialogOpen(true);
    };

    const handleBulkDelete = () => {
        if (!isAnyRowSelected(rowSelectionModel, rows)) {
            toast.error('No rows selected.');
            return;
        }
        setDeleteType('bulk');
        setConfirmDialogOpen(true);
    };

    const executeDelete = useCallback(async () => {
        if (!canDelete) return;
        try {
            if (deleteType === 'single') {
                if (!idToDelete) return;
                await api.delete(`/collectiondefs/${idToDelete}/permanent`);
                setRows(prev => prev.filter(r => r._id !== idToDelete));
                toast.success('Permanently deleted.');
            } else if (deleteType === 'bulk') {
                const selectedIds = getSelectedIds(rowSelectionModel, rows);
                if (!selectedIds.length) return;
                await api.put('/collectiondefs/bulk-soft-delete', { ids: selectedIds });
                setRows(prev => prev.filter(r => !selectedIds.includes(r._id)));
                toast.success(`Archived ${selectedIds.length} collection(s).`);
                setRowSelectionModel({ type: 'include', ids: new Set() });
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Operation failed.');
        }
    }, [
        canDelete, 
        deleteType, 
        idToDelete, 
        rowSelectionModel, 
        rows
    ]);

    const columns = useMemo(() => {
        const activeCount = [canEdit, canDelete, canOpen].filter(Boolean).length;
        const actionWidth = 65 + activeCount * 25;

        return [
            { field: '_id', headerName: 'ID', flex: 1 },
            { field: 'name', headerName: 'Name (slug)', flex: 2 },
            { field: 'label', headerName: 'Label', flex: 2 },
            {
                field: 'isActive',
                headerName: 'IsActive',
                flex: 1,
                renderCell: (params) => (
                    <Chip
                        label={params.value ? 'Active' : 'Inactive'}
                        color={params.value ? 'success' : 'default'}
                        size="small"
                    />
                ),
            },
            {
                field: '_actions',
                headerName: 'Action',
                width: actionWidth,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={1}
                        sx={{ width: "100%", height: "100%" }}
                    >
                        <Tooltip title="Open collection" arrow>
                            <IconButton
                                aria-label="Open collection"
                                onClick={() => navigate(`/modules/${params.row.name}`)}
                            >
                                <OpenInNew />
                            </IconButton>
                        </Tooltip>
                        {canEdit && (
                            <Tooltip title="Edit" arrow>
                                <IconButton
                                    aria-label="Edit"
                                    sx={{ color: "info.main" }}
                                    onClick={() => navigate(`/collections/${params.row._id}`)}
                                >
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                        )}
                        {canDelete && (
                            <Tooltip title="Permanently delete" arrow>
                                <IconButton
                                    aria-label="Permanently delete"
                                    sx={{ color: "error.main" }}
                                    onClick={() => handleDeleteClick(params.row._id)}
                                >
                                    <DeleteForever />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                ),
            },
        ];
    }, [
        navigate,
        canEdit,
        canDelete,
        canOpen,
    ]);
    
    if (loading) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <CircularProgress />
        </FlexCenter>
    );

    if (error) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <Typography color="error">{error?.message}</Typography>
        </FlexCenter>
    );
    

    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Collection Definitions"
                    subtitle="List of Collections"
                />
                <Box display="flex" alignItems="center" gap={1}>
                    {!isMobile && canDelete && (
                        <Tooltip title="Bulk archive selected" arrow>
                            <span>
                                <StyledButton
                                    disabled={!isAnyRowSelected(rowSelectionModel, rows)}
                                    onClick={handleBulkDelete}
                                >
                                    <Archive />
                                </StyledButton>
                            </span>
                        </Tooltip>
                    )}
                    {canCreate && (
                        <Tooltip title="Create" arrow>
                            <span>
                                <StyledButton onClick={() => navigate('/collections/new')}>
                                    <Add />
                                </StyledButton>
                            </span>
                        </Tooltip>
                    )}
                </Box>
            </FlexBetween>

            <Box
                display="grid"
                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                gap={2}
            >
                <Box
                    ref={scrollRef}
                    height={LAYOUT_HEIGHT}  
                    sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
                >
                    {isMobile ? (
                        <CardList
                            rows={rows}
                            primaryField="label"
                            secondaryField="name"
                            fields={[]}
                            canView
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onView={(id) => {
                                const r = rows.find(row => row._id === id);
                                if (r) navigate(`/modules/${r.name}`);
                            }}
                            onEdit={(id) => navigate(`/collections/${id}`)}
                            onDelete={(id) => handleDeleteClick(id)}
                        />
                    ) : (
                        
                        <DataGridComponent
                            rows={rows}
                            columns={columns}
                            columnVisibilityModel={columnVisibilityModel}
                            setColumnVisibilityModel={setColumnVisibilityModel}
                            loading={loading}
                            props={{
                                checkboxSelection: true,
                                disableRowSelectionOnClick: true,
                                onRowSelectionModelChange: setRowSelectionModel,
                                rowSelectionModel,
                            }}
                        />                        
                    )}

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            </Box>

            <DeleteConfirm
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={executeDelete}
                deleteType={deleteType}
            />
        </Box>
    );
};

export default CollectionDefs;
